import { useCallback, useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { getNextInternalRef } from "../../../lib/api";
import { Modal } from "../../../components/Modal";
import { useFormHelp, type FormHelpMap } from "../../../components/formHelp";
import { useJobForm } from "../useJobForm";
import {
  PRIORITIES,
  REPORTED_BY,
  formatFileSize,
  validateJobDraft,
  validateJobScopeDraft,
  type AttachmentDraft,
  type JobDraft,
} from "../lib/jobForm";
import { ActivityPage } from "./ActivityPage";
import { AttachmentsPage, ATTACHMENT_ACCEPT } from "./AttachmentsPage";
import { CommentsPage } from "./CommentsPage";
import { FormPageNav } from "./FormPageNav";
import { MemberField } from "./MemberField";
import { MoneyInput } from "../../../components/MoneyInput";
import { ScopeCostingPage } from "./ScopeCostingPage";
import { SchedulePage } from "./SchedulePage";
import { TasksPage } from "./TasksPage";
import { checklistCounts, validateChecklists } from "../lib/checklists";
import { validateJobSchedule } from "../lib/schedule";
import { ChipGroup } from "./ChipGroup";
import { JobTimingFields } from "./JobTimingFields";
import { ClientContactField, type ClientContactFieldValue } from "../../clients/components/ClientContactField";
import { PropertyAddressField } from "./PropertyAddressField";
import styles from "./NewJobForm.module.css";

/** Merges a partial change into the draft. Supplied by {@link useJobForm}. */
type PatchDraft = (partial: Partial<JobDraft>) => void;

/**
 * Keys of the built-in workflow pages. "details" is the base page; every other
 * key is an optional extension registered alongside it. "comments" and
 * "activity" are only meaningful for a persisted job — hosts pass them via
 * `pages` in edit mode.
 */
export type JobFormPageKey =
  | "details"
  | "scope"
  | "tasks"
  | "schedule"
  | "attachments"
  | "comments"
  | "activity";

const PAGE_LABELS: Record<JobFormPageKey, string> = {
  details: "Details",
  scope: "Scope",
  tasks: "Tasks",
  schedule: "Schedule",
  attachments: "Attachments",
  comments: "Comments",
  activity: "Activity",
};

/** The default workflow: the base page plus every built-in extension page. */
const DEFAULT_PAGES: readonly JobFormPageKey[] = ["details", "scope", "tasks", "schedule", "attachments"];

export interface NewJobFormProps {
  /** Seeds the draft when editing an existing job card. */
  initialValue?: Partial<JobDraft>;
  /** The dialog's accessible name (e.g. "New job" / "Edit job"). Not shown — the tab strip takes the header. */
  title: string;
  submitLabel: string;
  /** Called with the validated draft plus staged files; the host uploads after persisting the job. */
  onSubmit: (draft: JobDraft, files: Map<string, File>) => void | Promise<void>;
  /** Closes the dialog without submitting (the header close button and Escape). */
  onCancel?: () => void;
  /**
   * Pages in the multi-page workflow, in order. The base "details" page is
   * always included; extension pages ("scope" today, future pages later) may
   * be dropped per role/feature configuration without affecting the base
   * form. Defaults to the full workflow.
   */
  pages?: readonly JobFormPageKey[];
  /** Persisted job id — supplied when editing; hides Page 2's documents section when absent. */
  jobId?: string;
  /** The job's current lifecycle status (drives invoice availability). */
  jobStatus?: string;
  /** Called when a quote is accepted in the documents section (board refresh hook). */
  onQuoteAccepted?: () => void;
  /**
   * Popover help attached one level above the labels, keyed by control name
   * (or exact label text); see {@link FormHelpMap}. Fields without a key get
   * no trigger, so the map doubles as an on/off switch per field.
   */
  help?: FormHelpMap;
}

/**
 * New Job form, structured as a multi-page workflow inside its own {@link Modal}.
 * The tab strip takes the modal header (in place of a title) and the workflow
 * actions take the pinned footer, so both stay put while the fields scroll.
 *
 * Page 1 (Details) is the primary/base form and works on its own; each
 * later page is a complementary extension rendered from the same shared draft,
 * so entered values persist when moving between pages. Validation is scoped to
 * the pages actually enabled — extension pages never block the base job unless
 * a row the user started pricing is incoherent. The page list is data, not
 * control flow: hosts hide or add pages via the `pages` prop, and page bodies
 * are self-contained sections, so new pages attach without touching Page 1.
 *
 * @param {NewJobFormProps} props - Seed draft, submit label, page list, and lifecycle callbacks.
 */
export function NewJobForm({
  initialValue,
  title,
  submitLabel,
  onSubmit,
  onCancel,
  pages = DEFAULT_PAGES,
  jobId,
  jobStatus,
  onQuoteAccepted,
  help,
}: NewJobFormProps) {
  const formId = useId();
  // Normalised so the base page is always first; extension pages follow.
  const pageKeys = useMemo(
    () => ["details", ...pages.filter((key) => key !== "details")] as JobFormPageKey[],
    [pages],
  );
  const [pageIndex, setPageIndex] = useState(0);
  const activeIndex = Math.min(pageIndex, pageKeys.length - 1);
  const activeKey = pageKeys[activeIndex];
  const isLastPage = activeIndex >= pageKeys.length - 1;

  // Submit gate built from the enabled pages only: a hidden page can never
  // block the base job. Referentially stable for useJobForm's callback deps.
  const validate = useCallback(
    (draft: JobDraft): string => {
      const base = validateJobDraft(draft);
      if (base) return base;
      if (pageKeys.includes("scope")) {
        const scopeError = validateJobScopeDraft(draft.scope);
        if (scopeError) return scopeError;
      }
      if (pageKeys.includes("tasks")) {
        const tasksError = validateChecklists(draft.checklists);
        if (tasksError) return tasksError;
      }
      if (pageKeys.includes("schedule")) return validateJobSchedule(draft.schedule);
      return "";
    },
    [pageKeys],
  );

  const { draft, error, submitting, patch, setTiming, addAttachments, removeAttachment, handleSubmit } =
    useJobForm({ initialValue, jobId, onSubmit, validate });

  // Form Help convention: decorates matching labels inside this form with
  // popover triggers; content comes from the upstream `help` map only.
  const formRef = useRef<HTMLFormElement | null>(null);
  useFormHelp(formRef, help);

  // Submitting from a later page flips to the page that owns the first
  // failure, so the error and its fields are on screen together.
  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    if (validateJobDraft(draft)) {
      setPageIndex(0);
    } else if (pageKeys.includes("scope") && validateJobScopeDraft(draft.scope)) {
      setPageIndex(pageKeys.indexOf("scope"));
    } else if (pageKeys.includes("tasks") && validateChecklists(draft.checklists)) {
      setPageIndex(pageKeys.indexOf("tasks"));
    } else if (pageKeys.includes("schedule") && validateJobSchedule(draft.schedule)) {
      setPageIndex(pageKeys.indexOf("schedule"));
    }
    void handleSubmit(event);
  };

  const taskCounts = checklistCounts(draft.checklists);
  const tabNav =
    pageKeys.length > 1 ? (
      <FormPageNav
        pages={pageKeys.map((key) => ({ key, label: key === "tasks" ? `Tasks ${taskCounts.completed}/${taskCounts.total}` : PAGE_LABELS[key] }))}
        current={activeIndex}
        onSelect={setPageIndex}
      />
    ) : undefined;

  return (
    <Modal
      title={title}
      size="lg"
      onClose={onCancel ?? (() => undefined)}
      headerSlot={tabNav}
      footer={
        <>
          {onCancel && (
            <button type="button" className={styles.cancel} onClick={onCancel}>
              Cancel
            </button>
          )}
          {activeIndex > 0 && (
            <button type="button" className={styles.cancel} onClick={() => setPageIndex(activeIndex - 1)}>
              Back
            </button>
          )}
          {!isLastPage && (
            <button type="button" className={styles.submit} onClick={() => setPageIndex(activeIndex + 1)}>
              Next: {PAGE_LABELS[pageKeys[activeIndex + 1]]}
            </button>
          )}
          {isLastPage && (
            <button type="submit" form={formId} className={styles.submit} disabled={submitting}>
              {submitting ? "Submitting…" : submitLabel}
            </button>
          )}
        </>
      }
    >
      <form id={formId} ref={formRef} className={styles.root} onSubmit={handleFormSubmit} noValidate>
        <fieldset className={styles.fieldset} disabled={submitting}>
          {activeKey === "scope" ? (
            <ScopeCostingPage draft={draft} patch={patch} jobId={jobId} jobStatus={jobStatus} onQuoteAccepted={onQuoteAccepted} />
          ) : activeKey === "tasks" ? (
            <TasksPage checklists={draft.checklists} onChange={(checklists) => patch({ checklists })} />
          ) : activeKey === "schedule" ? (
            <SchedulePage draft={draft} patch={patch} />
          ) : activeKey === "attachments" ? (
            <AttachmentsPage
              attachments={draft.attachments}
              jobId={jobId}
              onAdd={addAttachments}
              onRemove={removeAttachment}
            />
          ) : activeKey === "comments" && jobId ? (
            <CommentsPage jobId={jobId} />
          ) : activeKey === "activity" && jobId ? (
            <ActivityPage jobId={jobId} />
          ) : (
            <>
              <ClientPropertySection draft={draft} patch={patch} />
              <IssueSection draft={draft} patch={patch} />

              {/* Carries its own <legend>, so this section supplies the rule above it only. */}
              <section className={styles.section}>
                <JobTimingFields value={draft.timing} onChange={setTiming} />
              </section>

              <SiteAccessSection draft={draft} patch={patch} />
              <ReferencesSection draft={draft} patch={patch} jobId={jobId} />
              <AttachmentsSection
                attachments={draft.attachments}
                jobId={jobId}
                onAdd={addAttachments}
                onRemove={removeAttachment}
                onOpenGallery={
                  pageKeys.includes("attachments")
                    ? () => setPageIndex(pageKeys.indexOf("attachments"))
                    : undefined
                }
              />
              <ApprovalSection draft={draft} patch={patch} />
              {/* Server data, not draft fields: members only exist on a persisted
                  job, so the section mounts in edit mode and PUTs its own changes. */}
              {jobId && <MemberField jobId={jobId} />}
            </>
          )}
        </fieldset>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}

interface DraftSectionProps {
  draft: JobDraft;
  patch: PatchDraft;
}

/**
 * Property address and the gate confirming it is the right one, then the client
 * and contact for the job.
 */
function ClientPropertySection({ draft, patch }: DraftSectionProps) {
  // `null` means the field no longer shows the chosen client/contact, so the id
  // has to go with it — a draft must never carry a selection the user cannot see.
  const handleClientSelect = (client: ClientContactFieldValue | null): void =>
    patch(
      client
        ? { clientId: client.id, clientName: client.name }
        : { clientId: undefined, clientName: "", clientContactId: undefined, clientContactName: "" },
    );
  const handleContactSelect = (contact: ClientContactFieldValue | null): void =>
    patch(
      contact
        ? { clientContactId: contact.id, clientContactName: contact.name }
        : { clientContactId: undefined, clientContactName: "" },
    );
  // Changing the address invalidates the confirmation, including when the new
  // text came from the autocomplete: picking a row off a list is not the same
  // act as vouching that it is the right property.
  const handlePropertyAddressChange = (address: string): void =>
    patch({ propertyAddress: address, propertyConfirmed: false });
  const handleConfirmProperty = (): void => patch({ propertyConfirmed: !draft.propertyConfirmed });

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Property and client</h3>

      <PropertyAddressField value={draft.propertyAddress} onChange={handlePropertyAddressChange} />
      <label className={styles.gate}>
        <input
          type="checkbox"
          className={styles.checkbox}
          name="propertyConfirmed"
          checked={draft.propertyConfirmed}
          disabled={!draft.propertyAddress.trim()}
          onChange={handleConfirmProperty}
        />
        <span className={styles.label}>I confirm this is the right property</span>
      </label>

      <ClientContactField
        clientId={draft.clientId}
        clientName={draft.clientName}
        clientContactId={draft.clientContactId}
        clientContactName={draft.clientContactName}
        onSelectClient={handleClientSelect}
        onSelectContact={handleContactSelect}
      />
    </section>
  );
}

/** What is wrong, how urgent it is, and who reported it. */
// Default resting height for the issue textarea; it grows elastically with
// content up to ISSUE_TEXTAREA_MAX before scrolling.
const ISSUE_TEXTAREA_MIN = 84;
const ISSUE_TEXTAREA_MAX = 275;

function IssueSection({ draft, patch }: DraftSectionProps) {
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const resizeDescription = useCallback((el: HTMLTextAreaElement): void => {
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, ISSUE_TEXTAREA_MIN), ISSUE_TEXTAREA_MAX);
    el.style.height = `${next}px`;
  }, []);

  // Keep the height in sync when the value changes from outside (e.g. draft
  // hydration) as well as on direct typing.
  useEffect(() => {
    if (descriptionRef.current) resizeDescription(descriptionRef.current);
  }, [draft.description, resizeDescription]);

  const handleDescriptionChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    resizeDescription(event.target);
    patch({ description: event.target.value });
  };
  const handlePriorityChange = (value: JobDraft["priority"]): void => patch({ priority: value });
  const handleReportedByChange = (value: JobDraft["reportedBy"]): void => patch({ reportedBy: value });

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>The issue</h3>
      <label className={styles.field}>
        <span className={styles.label}>What is the issue?</span>
        <textarea
          ref={descriptionRef}
          className={styles.textarea}
          name="description"
          rows={4}
          value={draft.description}
          onChange={handleDescriptionChange}
        />
      </label>

      <div className={styles.row}>
        <ChipGroup
          legend="Priority"
          name="priority"
          options={PRIORITY_OPTIONS}
          value={draft.priority}
          onChange={handlePriorityChange}
        />
        <ChipGroup
          legend="Reported by"
          name="reportedBy"
          options={REPORTED_BY_OPTIONS}
          value={draft.reportedBy}
          onChange={handleReportedByChange}
        />
      </div>
    </section>
  );
}

/**
 * Internal/external references and the internal price estimate. The internal
 * reference is system-assigned, never typed: read-only display of the job's
 * fixed number on edit, and a preview of the next number on create (the
 * server allocates the real one inside the create transaction).
 */
function ReferencesSection({ draft, patch, jobId }: DraftSectionProps & { jobId?: string }) {
  const handleRefExternalChange = (event: ChangeEvent<HTMLInputElement>): void =>
    patch({ refExternal: event.target.value });

  const [nextRef, setNextRef] = useState("");
  useEffect(() => {
    if (jobId) return;
    let cancelled = false;
    getNextInternalRef()
      .then(({ refInternal }) => {
        if (!cancelled) setNextRef(refInternal);
      })
      .catch(() => {
        /* preview only — the server assigns the real number on save */
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>References &amp; estimate</h3>
      <div className={styles.row}>
        <label className={styles.field}>
          <span className={styles.label}>Internal reference</span>
          <input
            className={styles.input}
            name="refInternal"
            value={jobId ? draft.refInternal : nextRef}
            placeholder={jobId ? "Not assigned" : "Auto-assigned on save"}
            readOnly
          />
          <span className={styles.note}>
            {jobId
              ? "Fixed for the life of the job; always shown to the client and vendor."
              : `Assigned automatically on save${nextRef ? ` — next is ${nextRef}` : ""}; always shown to the client and vendor.`}
          </span>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>External reference</span>
          <input
            className={styles.input}
            name="refExternal"
            value={draft.refExternal}
            placeholder="e.g. agent work order #"
            onChange={handleRefExternalChange}
          />
          <span className={styles.note}>Optional — supplied by the client (e.g. a PDF work order #).</span>
        </label>
      </div>
      <label className={styles.field}>
        <span className={styles.label}>Price estimate (AUD)</span>
        <MoneyInput
          name="priceEstimate"
          valueCents={draft.priceEstimate}
          onChange={(priceEstimate) => patch({ priceEstimate })}
          ariaLabel="Price estimate in dollars"
        />
        <span className={styles.note}>Internal estimate; displayed on the board.</span>
      </label>
    </section>
  );
}

/** Who is on site and how to get in. */
function SiteAccessSection({ draft, patch }: DraftSectionProps) {
  const handleOccupantNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    patch({ occupantName: event.target.value });
  const handleOccupantPhoneChange = (event: ChangeEvent<HTMLInputElement>): void =>
    patch({ occupantPhone: event.target.value });
  const handleAccessNotesChange = (event: ChangeEvent<HTMLTextAreaElement>): void =>
    patch({ accessNotes: event.target.value });

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Site access</h3>
      <div className={styles.row}>
        <label className={styles.field}>
          <span className={styles.label}>Occupant name</span>
          <input
            className={styles.input}
            name="occupantName"
            value={draft.occupantName}
            placeholder="Who is on site"
            onChange={handleOccupantNameChange}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Occupant phone</span>
          <input
            className={styles.input}
            name="occupantPhone"
            type="tel"
            value={draft.occupantPhone}
            placeholder="Best contact number"
            onChange={handleOccupantPhoneChange}
          />
        </label>
      </div>

      <label className={styles.field}>
        <span className={styles.label}>Access instructions</span>
        <textarea
          className={styles.textarea}
          name="accessNotes"
          rows={3}
          value={draft.accessNotes}
          placeholder="Keys, alarm code, pets, parking, tenant present"
          onChange={handleAccessNotesChange}
        />
        <span className={styles.note}>Optional.</span>
      </label>
    </section>
  );
}

interface AttachmentsSectionProps {
  attachments: readonly AttachmentDraft[];
  /** Persisted job id — present in edit mode, where rows download from the API. */
  jobId?: string;
  onAdd: (files: FileList | null) => void;
  onRemove: (id: string) => void;
  /** Jumps to the Attachments page; absent when that page is not in the workflow. */
  onOpenGallery?: () => void;
}

/**
 * Staged photos and documents. Files are held in the draft until submit.
 *
 * This is the compact view of the same list the Attachments page renders — a
 * quick add-and-check while filling in the job, not the place to review files.
 * The two read the same draft array, so a file added here appears there.
 */
function AttachmentsSection({ attachments, jobId, onAdd, onRemove, onOpenGallery }: AttachmentsSectionProps) {
  // Clearing the input is what makes re-picking the same file fire `change`
  // again; without it a removed attachment cannot be added back.
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onAdd(event.target.files);
    event.target.value = "";
  };

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Attachments</h3>
      <div className={styles.attach}>
        <input
          id="job-attachments"
          className={styles.fileInput}
          name="attachments"
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          onChange={handleInputChange}
        />
        <label htmlFor="job-attachments" className={styles.fileLabel}>
          Add photo or document
        </label>
        <span className={styles.attachLabel}>
          {attachments.length
            ? `${attachments.length} attachment${attachments.length === 1 ? "" : "s"} ready`
            : "No attachments yet"}
        </span>
        {onOpenGallery && (
          <button type="button" className={styles.attachLink} onClick={onOpenGallery}>
            {attachments.length ? "View in gallery" : "Open attachments page"}
          </button>
        )}
      </div>
      {attachments.length > 0 && (
        <ul className={styles.fileList}>
          {attachments.map((attachment) => (
            <AttachmentRow
              key={attachment.id}
              attachment={attachment}
              jobId={jobId}
              onRemove={onRemove}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** The quote-approval gate that decides whether work can start before a quote. */
function ApprovalSection({ draft, patch }: DraftSectionProps) {
  const handleQuoteGateChange = (): void => patch({ requireQuoteApproval: !draft.requireQuoteApproval });

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Approval</h3>
      <label className={styles.gate}>
        <input
          type="checkbox"
          className={styles.checkbox}
          name="requireQuoteApproval"
          checked={draft.requireQuoteApproval}
          onChange={handleQuoteGateChange}
        />
        <span className={styles.gateStack}>
          <span className={styles.label}>Require quote approval</span>
          <span className={styles.note}>
            Status starts in Drafts and no work begins until a quote is approved.
          </span>
        </span>
      </label>
    </section>
  );
}

interface AttachmentRowProps {
  attachment: AttachmentDraft;
  jobId?: string;
  onRemove: (id: string) => void;
}

/**
 * One staged or persisted file. Persisted rows download from the API (the
 * session cookie authenticates the navigation); staged rows are plain text.
 */
function AttachmentRow({ attachment, jobId, onRemove }: AttachmentRowProps) {
  const handleRemove = (): void => onRemove(attachment.id);

  return (
    <li className={styles.fileRow}>
      {attachment.persisted && jobId ? (
        <a
          className={styles.fileLink}
          href={`/api/jobs/${jobId}/attachments/${attachment.id}`}
          download={attachment.name}
        >
          {attachment.name}
        </a>
      ) : (
        <span className={styles.fileName}>{attachment.name}</span>
      )}
      <span className={styles.fileMeta}>{formatFileSize(attachment.size)}</span>
      <button type="button" className={styles.fileRemove} onClick={handleRemove}>
        Remove
      </button>
    </li>
  );
}

// Built once at module scope: the option lists are constant, and rebuilding them
// per render handed ChipGroup a new array on every keystroke.
const PRIORITY_OPTIONS = PRIORITIES.map((priority) => ({ value: priority, label: priority }));
const REPORTED_BY_OPTIONS = REPORTED_BY.map((reportedBy) => ({ value: reportedBy, label: reportedBy }));
